import { useCallback, useEffect, useState, createContext, useContext } from "react";

export type ThemeMode = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "growtify-cockpit-theme";

function resolveSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

function readStored(): ThemeMode {
  if (typeof window === "undefined") return "system";
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "light" || v === "dark" || v === "system" ? v : "system";
}

function applyTheme(resolved: ResolvedTheme, animate = true) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (animate) {
    root.classList.add("theme-transition");
    window.setTimeout(() => root.classList.remove("theme-transition"), 200);
  }
  root.setAttribute("data-theme", resolved);
}

interface ThemeCtx {
  mode: ThemeMode;          // user choice (light | dark | system)
  resolved: ResolvedTheme;  // actually-applied theme (light | dark)
  setMode: (m: ThemeMode) => void;
  cycle: () => void;        // light → dark → system → light
}

const ThemeContext = createContext<ThemeCtx | null>(null);

export function useTheme(): ThemeCtx {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => readStored());
  const [resolved, setResolved] = useState<ResolvedTheme>(() =>
    readStored() === "system" ? resolveSystemTheme() : (readStored() as ResolvedTheme)
  );

  // Apply on mount + whenever mode changes.
  useEffect(() => {
    const next: ResolvedTheme =
      mode === "system" ? resolveSystemTheme() : mode;
    setResolved(next);
    applyTheme(next, true);
    window.localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  // React to system preference changes when in system mode.
  useEffect(() => {
    if (mode !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = () => {
      const next: ResolvedTheme = mq.matches ? "light" : "dark";
      setResolved(next);
      applyTheme(next, true);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [mode]);

  const setMode = useCallback((m: ThemeMode) => setModeState(m), []);
  const cycle = useCallback(() => {
    setModeState((m) => (m === "light" ? "dark" : m === "dark" ? "system" : "light"));
  }, []);

  return (
    <ThemeContext.Provider value={{ mode, resolved, setMode, cycle }}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Reads the persisted theme choice synchronously and applies it before React
 * boots. Call this from main.tsx top-of-file to prevent the dark-flash on
 * first paint when user has set "light".
 */
export function bootstrapTheme() {
  if (typeof window === "undefined") return;
  const stored = readStored();
  const resolved: ResolvedTheme =
    stored === "system" ? resolveSystemTheme() : stored;
  applyTheme(resolved, false);
}
